import { useEffect, useRef } from 'react';
import { useTooltip } from '../context/TooltipContext';

export const useGlobalTitleInterceptor = () => {
  const { showTooltip, hideTooltip, updatePosition } = useTooltip();
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentElementRef = useRef<Element | null>(null);

  useEffect(() => {
    
    // Only skip on actual mobile devices, not desktop browsers with touch support
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobileDevice) {
      console.log('[Tooltip] Mobile device detected, skipping tooltip interception');
      return;
    }

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Find the closest element with a title attribute
      const elementWithTitle = target.closest('[title]');
      
      if (!elementWithTitle) {
        return;
      }

      // Skip disabled elements
      if (
        elementWithTitle.hasAttribute('disabled') ||
        elementWithTitle.classList.contains('pointer-events-none')
      ) {
        return;
      }

      const title = elementWithTitle.getAttribute('title');
      
      if (!title || title.trim() === '') {
        return;
      }

      // Store the original title and remove it temporarily
      elementWithTitle.setAttribute('data-original-title', title);
      elementWithTitle.removeAttribute('title');
      
      currentElementRef.current = elementWithTitle;

      // Show tooltip after delay
      showTimeoutRef.current = setTimeout(() => {
        showTooltip(title, event.clientX, event.clientY);
      }, 300);
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (currentElementRef.current) {
        // Update position while hovering
        updatePosition(event.clientX, event.clientY);
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const target = event.target as Element;
      const relatedTarget = event.relatedTarget as Element | null;
      
      // Find element with data-original-title (the one we're tracking)
      const elementWithTitle = target.closest('[data-original-title]');

      if (!elementWithTitle) {
        return;
      }

      // Check if we're moving to a child element - if so, don't hide tooltip
      if (relatedTarget && elementWithTitle.contains(relatedTarget)) {
        return;
      }

      // Cancel pending show timeout
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }

      // Restore the original title attribute
      const originalTitle = elementWithTitle.getAttribute('data-original-title');
      if (originalTitle) {
        elementWithTitle.setAttribute('title', originalTitle);
        elementWithTitle.removeAttribute('data-original-title');
      }

      hideTooltip();
      currentElementRef.current = null;
    };

    // Add event listeners with capture phase
    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    console.log('[Tooltip] Event listeners registered successfully on document');

    // Cleanup
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
    };
  }, [showTooltip, hideTooltip, updatePosition]);
};
